// ─── EnvoyerAFacturer.jsx ────────────────────────────────────────────
// Le bouton par lequel Anissa passe la main a Benoit pour la facturation.
//
// Elle soigne, il facture. Depuis le 08.08 il n'a plus de compte sur ce
// SaaS, donc il ne peut pas venir voir : c'est ce depot qui l'informe.
//
// ⚠ ON VERIFIE D'ABORD SI UNE DEMANDE EXISTE DEJA. Sans ca, un rechargement
// de page suivi d'un second clic enverrait deux fois la meme facture, et
// personne ne le verrait avant que la cliente ne recoive deux courriers.
//
// Le libelle envoye ne nomme jamais une analyse : voir `demandeFacturation.js`.

import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';
import {
  construireDemande, messageAvertissement, messageRefusFacturation,
} from '../services/demandeFacturation';
import { PACK_DEFINITIONS } from '../services/packSystem';

const OR = '#8a5a12';
const VERT = '#2d5a3d';
const ROUGE = '#a33b2c';

// Jusqu'a 6, la duree du plus long accompagnement. Au-dela, la derniere
// echeance courrait apres la fin du suivi, c'est-a-dire au moment ou l'on a
// le moins de prise si quelque chose se passe mal.
const CHOIX_PAIEMENT = [
  { valeur: 1, label: 'Comptant' },
  { valeur: 2, label: 'En 2 fois' },
  { valeur: 3, label: 'En 3 fois' },
  { valeur: 4, label: 'En 4 fois' },
  { valeur: 6, label: 'En 6 fois' },
];

/**
 * La part du pack sur laquelle une remise peut porter.
 *
 * ⚠ PAS le prix du pack. Le pack inclut un credit analyses (250 sur le
 * 3 mois, 400 sur le 6 mois) qui part droit chez le laboratoire. Un
 * pourcentage applique au prix entier entamerait ce credit, donc la marge
 * labo — exactement ce que la doctrine du 05.08 ecarte.
 */
export function partNegociable(packType) {
  const pack = PACK_DEFINITIONS[packType];
  if (!pack) return 0;
  return Math.max(0, (Number(pack.price) || 0) - (Number(pack.includedAnalysisCreditChf) || 0));
}

/** Ce que donne un pourcentage sur la part negociable, au franc. */
export function remisePourPourcentage(packType, pourcentage) {
  const base = partNegociable(packType);
  if (!base || !Number.isFinite(pourcentage)) return 0;
  return Math.round((base * pourcentage) / 100);
}

const POURCENTAGES = [10, 15, 20];

export default function EnvoyerAFacturer({ client, plan = null }) {
  const [mensualites, setMensualites] = useState(1);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState(null);
  const [dejaEnvoyee, setDejaEnvoyee] = useState(undefined); // undefined = on ne sait pas encore
  // La remise vit sur la fiche cliente : elle vaut pour toute facture a
  // venir, pas seulement celle-ci. On en garde une copie locale pour que le
  // total se recalcule sans recharger la page.
  const [remise, setRemise] = useState({
    chf: client?.remise_chf != null ? String(client.remise_chf) : '',
    motif: client?.remise_motif || '',
  });
  const [editionRemise, setEditionRemise] = useState(false);
  const [remiseErreur, setRemiseErreur] = useState(null);
  const [enregistrement, setEnregistrement] = useState(false);

  useEffect(() => {
    let annule = false;
    if (!client?.id) { setDejaEnvoyee(null); return undefined; }
    supabase
      .from('facturation_demandes')
      .select('id,creee_le,montant_chf,statut')
      .eq('client_id', client.id)
      .order('creee_le', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data, error }) => {
        if (annule) return;
        // Sur echec on reste a `undefined` : le bouton se desactive plutot
        // que de risquer un doublon sur une information qu'on n'a pas.
        setDejaEnvoyee(error ? undefined : (data || null));
      });
    return () => { annule = true; };
  }, [client?.id]);

  // Le calcul voit la remise telle qu'elle est a l'ecran, pas telle qu'elle
  // etait au chargement : sans ca, poser une remise n'aurait aucun effet
  // visible avant un rechargement.
  const clientAvecRemise = {
    ...client,
    remise_chf: remise.chf === '' ? null : Number(remise.chf),
    remise_motif: remise.motif || null,
  };
  const resultat = construireDemande({ client: clientAvecRemise, plan, mensualites });
  const negociable = partNegociable(client?.pack_type || plan?.pack_type);

  const enregistrerRemise = async (montant, motif) => {
    if (!client?.id) return;
    setRemiseErreur(null);
    setEnregistrement(true);
    try {
      const { error } = await supabase
        .from('clients')
        .update({
          remise_chf: montant,
          remise_motif: motif,
          remise_posee_le: montant ? new Date().toISOString() : null,
        })
        .eq('id', client.id);
      if (error) throw new Error(error.message);
      setRemise({ chf: montant ? String(montant) : '', motif: motif || '' });
      setEditionRemise(false);
    } catch (e) {
      setRemiseErreur(e?.message || 'Enregistrement impossible');
    } finally {
      setEnregistrement(false);
    }
  };

  const envoyer = async () => {
    if (!resultat.ok) return;
    setEnvoi(true);
    setErreur(null);
    try {
      // ⚠ LA FICHE D'ABORD, LA DEMANDE ENSUITE.
      //
      // 26.08.2026 — Guillemette. Benoit tape 284 dans le panneau, l'écran
      // affiche 1900, il transmet : la facture part juste. Mais le panneau
      // a son propre bouton « Enregistrer », qu'il n'avait pas cliqué. La
      // fiche est restée à 350 pendant que la facture disait 284, et RIEN
      // à l'écran ne pouvait le lui apprendre puisque le total affiché
      // venait de la copie locale.
      //
      // Une remise qu'on vient de facturer EST la remise de la cliente.
      // On l'écrit donc avant d'envoyer quoi que ce soit : si cette
      // écriture échoue, rien ne part, plutôt qu'une facture démentie par
      // la fiche qui l'a produite.
      const remisePosee = resultat.demande.remise_chf ?? null;
      if (remisePosee !== (client.remise_chf ?? null)) {
        const { error: erreurFiche } = await supabase
          .from('clients')
          .update({
            remise_chf: remisePosee,
            remise_motif: resultat.demande.remise_motif,
            remise_posee_le: remisePosee ? new Date().toISOString() : null,
          })
          .eq('id', client.id);
        if (erreurFiche) throw new Error(erreurFiche.message);
      }

      const { data, error } = await supabase
        .from('facturation_demandes')
        .insert(resultat.demande)
        .select('id,creee_le,montant_chf,statut')
        .single();
      if (error) throw new Error(error.message);
      setDejaEnvoyee(data);
    } catch (e) {
      setErreur(e?.message || 'L’envoi a échoué');
    } finally {
      setEnvoi(false);
    }
  };

  if (dejaEnvoyee) {
    const quand = new Date(dejaEnvoyee.creee_le).toLocaleDateString('fr-CH');
    const traitee = dejaEnvoyee.statut === 'facturee';
    return (
      <p style={{ fontSize: 12, color: traitee ? VERT : OR, margin: '10px 0 0' }}>
        {traitee
          ? `Facturée par Benoit · ${dejaEnvoyee.montant_chf} CHF, envoyée le ${quand}.`
          : `Transmise à Benoit le ${quand} · ${dejaEnvoyee.montant_chf} CHF. En attente de facturation.`}
      </p>
    );
  }

  // ─── La remise, en deux morceaux ────────────────────────────────
  //
  // ⚠ LE PANNEAU VIT HORS DU TEST `resultat.ok`.
  //
  // Il etait dedans. Saisir un montant sans avoir encore ecrit la raison
  // faisait refuser `construireDemande`, et le refus remplacait TOUT le
  // formulaire par son message rouge — y compris le champ en cours de
  // saisie et le bouton Annuler. Benoit s'est retrouve bloque, sans moyen
  // de corriger ni de revenir en arriere. Une erreur de saisie ne doit
  // jamais emporter le champ qui permet de la corriger.
  //
  // ⚠ LE BOUTON EST SEPARE DU PANNEAU, ET CE N'EST PAS UN DETAIL.
  //
  // Il vit dans la rangee d'actions, a cote de « Transmettre », pour qu'on
  // le voie : en petit lien souligne au milieu du bloc, Benoit avait du le
  // chercher, et Anissa ne l'aurait jamais trouve.
  //
  // La rangee entiere est donc sortie du test elle aussi. Sinon le bouton
  // disparaitrait a la seconde ou l'on saisit un montant sans motif, et on
  // reproduirait exactement le blocage qu'on vient de corriger.
  const boutonRemise = !editionRemise ? (
    <button
      type="button"
      onClick={() => setEditionRemise(true)}
      style={{
        fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
        border: `1px solid ${OR}`, background: 'rgba(138,90,18,.07)',
        color: OR, cursor: 'pointer',
      }}
    >
      {remise.chf ? `Remise · ${remise.chf} CHF` : 'Ajouter une remise'}
    </button>
  ) : null;

  const panneauRemise = (
    <>
  {/* ─── La remise ────────────────────────────────────────
      ⚠ Le pourcentage porte sur la part NEGOCIABLE, pas sur le prix
      du pack. Le pack inclut un credit analyses qui part droit chez
      le laboratoire ; un pourcentage sur le prix entier entamerait
      ce credit, donc l'argent qu'Anissa avance. Doctrine du 05.08. */}
  {editionRemise && (
    <div style={{
      marginTop: 10, padding: '10px 12px', borderRadius: 8,
      background: 'rgba(0,0,0,.03)', border: '1px solid rgba(0,0,0,.08)',
    }}>
      <p style={{ fontSize: '.74rem', color: '#666', margin: '0 0 8px', lineHeight: 1.5 }}>
        La remise porte sur l’accompagnement seul, jamais sur les analyses :
        celles-ci sont au prix du laboratoire. Part négociable de ce pack :{' '}
        <strong>{negociable} CHF</strong>.
      </p>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        {POURCENTAGES.map(p => {
          const montant = remisePourPourcentage(client?.pack_type || plan?.pack_type, p);
          return (
            <button
              key={p}
              type="button"
              onClick={() => setRemise(r => ({ ...r, chf: String(montant) }))}
              title={`${p} % de ${negociable} CHF`}
              style={{
                fontSize: '.74rem', padding: '3px 9px', borderRadius: 6,
                border: '1px solid rgba(0,0,0,.2)', background: '#fff',
                color: '#333', cursor: 'pointer',
              }}
            >
              −{p} % ({montant})
            </button>
          );
        })}
        <input
          type="number"
          min="0"
          step="10"
          value={remise.chf}
          onChange={(e) => setRemise(r => ({ ...r, chf: e.target.value }))}
          placeholder="CHF"
          style={{ width: 80, fontSize: '.78rem', padding: '4px 6px' }}
        />
      </div>

      <input
        type="text"
        value={remise.motif}
        onChange={(e) => setRemise(r => ({ ...r, motif: e.target.value }))}
        placeholder="Pourquoi cette remise ? (obligatoire)"
        style={{ width: '100%', marginTop: 8, fontSize: '.78rem', padding: '5px 7px' }}
      />

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 10 }}>
        <button
          type="button"
          disabled={enregistrement || !remise.chf || remise.motif.trim().length < 3}
          onClick={() => enregistrerRemise(Number(remise.chf), remise.motif.trim())}
          title={remise.motif.trim().length < 3
            ? 'Une remise sans raison ne se retrouve pas dans six mois'
            : undefined}
          style={{
            fontSize: '.78rem', padding: '5px 12px', borderRadius: 7,
            border: `1px solid ${VERT}`, background: 'rgba(45,90,61,.08)', color: VERT,
            opacity: (!remise.chf || remise.motif.trim().length < 3) ? 0.45 : 1,
            cursor: enregistrement ? 'wait' : 'pointer',
          }}
        >
          {enregistrement ? 'Enregistrement…' : 'Enregistrer'}
        </button>
        {client?.remise_chf != null && (
          <button
            type="button"
            disabled={enregistrement}
            onClick={() => enregistrerRemise(null, null)}
            style={{
              fontSize: '.78rem', padding: '5px 12px', borderRadius: 7,
              border: '1px solid rgba(0,0,0,.2)', background: '#fff',
              color: ROUGE, cursor: 'pointer',
            }}
          >
            Retirer
          </button>
        )}
        <button
          type="button"
          onClick={() => {
            setRemise({
              chf: client?.remise_chf != null ? String(client.remise_chf) : '',
              motif: client?.remise_motif || '',
            });
            setEditionRemise(false);
          }}
          style={{
            fontSize: '.78rem', padding: '5px 12px', borderRadius: 7,
            border: '1px solid rgba(0,0,0,.2)', background: '#fff',
            color: '#333', cursor: 'pointer',
          }}
        >
          Annuler
        </button>
      </div>

      {remiseErreur && (
        <p role="alert" style={{ fontSize: '.76rem', color: ROUGE, margin: '8px 0 0' }}>
          {remiseErreur}
        </p>
      )}
    </div>
  )}
    </>
  );


  return (
    <div style={{
      marginTop: 14, padding: '12px 16px', borderRadius: 10,
      background: 'rgba(0,0,0,.02)', border: '1px solid rgba(0,0,0,.1)',
    }}>
      <span style={{ fontWeight: 600, fontSize: '.85rem', color: '#333' }}>
        Transmettre à Benoit pour facturation
      </span>

      {!resultat.ok && (
        <p style={{ fontSize: '.78rem', color: ROUGE, margin: '6px 0 0', lineHeight: 1.5 }}>
          {messageRefusFacturation(resultat)}
        </p>
      )}

      {/* Le panneau reste manipulable meme quand le calcul refuse : c'est
          souvent la remise qu'il faut corriger. */}
      {panneauRemise}

      {resultat.ok && (
        <>
          <p style={{ fontSize: '.8rem', color: '#444', margin: '6px 0 0' }}>
            {resultat.demande.libelle} ·{' '}
            <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
              {resultat.demande.montant_chf} CHF
            </strong>
          </p>

          {resultat.demande.remise_chf > 0 && (
            <p style={{ fontSize: '.78rem', color: VERT, margin: '3px 0 0' }}>
              Remise de {resultat.demande.remise_chf} CHF déjà déduite ·{' '}
              <span style={{ color: '#777' }}>{resultat.demande.remise_motif}</span>
            </p>
          )}

          {resultat.avertissements.map(code => (
            <p key={code} style={{ fontSize: '.76rem', color: OR, margin: '5px 0 0', lineHeight: 1.5 }}>
              {messageAvertissement(code)}
            </p>
          ))}
        </>
      )}

      {/* La rangee d'actions vit HORS du test : le bouton de remise doit
          rester atteignable meme quand le calcul refuse, puisque c'est
          souvent la remise qui le fait refuser. */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
        {boutonRemise}
        {resultat.ok && (
          <>
            <label style={{ fontSize: '.78rem', color: '#666' }}>
              Paiement{' '}
              <select
                value={mensualites}
                onChange={(e) => setMensualites(Number(e.target.value))}
                style={{ fontSize: '.78rem', padding: '4px 6px' }}
              >
                {CHOIX_PAIEMENT.map(c => (
                  <option key={c.valeur} value={c.valeur}>{c.label}</option>
                ))}
              </select>
            </label>
            <button
              type="button"
              onClick={envoyer}
              disabled={envoi || dejaEnvoyee === undefined}
              title={dejaEnvoyee === undefined ? 'Vérification en cours…' : undefined}
              style={{
                fontSize: '.8rem', padding: '6px 14px', borderRadius: 8,
                border: `1px solid ${VERT}`, background: 'rgba(45,90,61,.08)',
                color: VERT,
                cursor: envoi || dejaEnvoyee === undefined ? 'wait' : 'pointer',
                opacity: dejaEnvoyee === undefined ? 0.6 : 1,
              }}
            >
              {envoi ? 'Envoi…' : 'Transmettre'}
            </button>
          </>
        )}
      </div>

      {erreur && (
        <p role="alert" style={{ fontSize: '.78rem', color: ROUGE, margin: '8px 0 0' }}>
          {erreur}
        </p>
      )}
    </div>
  );
}
